/**
 * useStartPlaybookForDeal
 *
 * Mutation que aciona a RPC `start_per_phase_playbook_for_deal` para iniciar
 * manualmente o playbook per_phase ativo do pipeline na fase atual do deal.
 * Após sucesso, invalida caches de cadência, atividades do deal e listagens
 * globais de atividades.
 *
 * Versão 1.0.0 — 2026-06-01
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { invalidateActivityListCaches } from '@/lib/cache/invalidateActivityListCaches';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useStartPlaybookForDeal');

// Resposta esperada da RPC
export interface StartPlaybookResult {
  cadence_id: string;
  steps_created: number;
}

interface MutationInput {
  dealId: string;
  playbookId?: string | null;
}

export function useStartPlaybookForDeal() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async ({ dealId, playbookId }: MutationInput): Promise<StartPlaybookResult> => {
      const { data, error } = await (supabase.rpc as any)(
        'start_per_phase_playbook_for_deal',
        {
          p_deal_id: dealId,
          p_playbook_id: playbookId ?? null,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        logger.error('Erro ao iniciar playbook manualmente:', error);
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      return row as StartPlaybookResult;
    },
    onSuccess: (_data, variables) => {
      // Invalida cadência, elegibilidade, atividades do deal e listagens globais
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', variables.dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', variables.dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-playbook-eligibility', variables.dealId, tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-activities', variables.dealId] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data', variables.dealId] });
      void invalidateActivityListCaches(queryClient);
    },
  });
}
